import React, { useCallback, useState, useMemo } from 'react';
import styled from 'styled-components';
import moment from 'moment';
import { getDurationFromMilliseconds } from '@ubeya/shared/utils/time';
import { ReactComponent as AddIcon } from '@ubeya/shared-web/assets/plus.svg';
import { isReadOnly, totalTime } from 'shared/utils/timesheet';
import BreakModal from '../ShiftDetails/BreakModal';
import BreakWarningIndicator from './BreakWarningIndicator';

const Wrapper = styled.span.attrs(() => ({ className: 'underline' }))`
  margin-right: 10px;
  line-height: 24rem;
`;

const PlusIcon = styled(AddIcon)`
  width: 14px;
  height: 14px;
`;

const AddButton = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  cursor: pointer;
  margin-right: 8px;

  &:hover {
    background: ${({ theme }) => theme.colors.grey1};
  }
`;

const BreaksRenderer = ({
  rowData,
  rowData: {
    subRows,
    summarized,
    branchId,
    breaktimes = [],
    isExternal,
    date,
    actualIn,
    actualOut,
    isPto,
    breakWarnings
  },
  columnData: {
    updateBreak,
    deleteOSBreak,
    updateOSBreaks,
    deleteBreak,
    readOnly = false,
    disableBreaksForExternal = false
  }
}) => {
  const [selectedBreak, setSelectedBreak] = useState();
  const handleSelectBreak = useCallback((e, breakItem) => {
    e.stopPropagation();
    setSelectedBreak({
      ...breakItem,
      endTime: breakItem.endTime && moment(breakItem.endTime).isValid() ? breakItem.endTime : undefined
    });
  }, []);

  const mappedBreaksToDuration = useMemo(
    () =>
      breaktimes
        ?.filter(
          ({ startTime, endTime }) => startTime && moment(startTime).isValid() && endTime && moment(endTime).isValid()
        )
        .map(({ startTime, endTime }) => totalTime(startTime, endTime))
        .reduce((all, duration) => all + duration, 0),
    [breaktimes]
  );

  const totalDurationOfBreaks = useMemo(
    () => (mappedBreaksToDuration === 0 ? '' : getDurationFromMilliseconds(mappedBreaksToDuration)),
    [mappedBreaksToDuration]
  );

  const displayBreaktimes = useMemo(
    () =>
      breaktimes?.length > 0 && (
        <Wrapper key={breaktimes.length - 1} onClick={(e) => handleSelectBreak(e, breaktimes?.[breaktimes.length - 1])}>
          {totalDurationOfBreaks}
        </Wrapper>
      ),
    [breaktimes, handleSelectBreak, totalDurationOfBreaks]
  );

  const displayBreakWarning = <BreakWarningIndicator breakWarnings={breakWarnings} />;

  const onConfirm = useCallback(
    (values) => {
      const updateParams = { ...rowData, breakId: selectedBreak?.id, ...values };

      if (isExternal) {
        return updateOSBreaks(updateParams);
      }
      return updateBreak(updateParams);
    },
    [isExternal, rowData, selectedBreak?.id, updateBreak, updateOSBreaks]
  );

  const onDelete = useCallback(() => {
    const deleteParams = {
      ...rowData,
      id: rowData?.id,
      timesheetId: selectedBreak?.timesheetId || rowData?.timesheetId,
      breakId: selectedBreak?.id,
      shiftId: rowData?.shiftId
    };

    if (isExternal) {
      return deleteOSBreak(deleteParams);
    }
    return deleteBreak(deleteParams);
  }, [deleteBreak, deleteOSBreak, isExternal, rowData, selectedBreak?.id, selectedBreak?.timesheetId]);

  if (isReadOnly(rowData, readOnly) || summarized || (isExternal && disableBreaksForExternal)) {
    return (
      <>
        {displayBreakWarning}
        {totalDurationOfBreaks}
      </>
    );
  }

  if (subRows || !actualIn || isPto) {
    return <></>;
  }

  return (
    <>
      {displayBreakWarning}
      <AddButton onClick={(e) => handleSelectBreak(e, {})}>
        <PlusIcon />
      </AddButton>

      {displayBreaktimes}

      {selectedBreak && (
        <BreakModal
          onClose={() => setSelectedBreak()}
          onConfirm={onConfirm}
          date={date}
          onDelete={onDelete}
          breaktimes={breaktimes}
          branchId={branchId}
          selectedBreak={selectedBreak}
          startTime={actualIn}
          endTime={actualOut}
        />
      )}
    </>
  );
};

export default BreaksRenderer;
